import _ from 'lodash';
import React, { useEffect } from 'react';
import type { ApolloError } from '@apollo/client';
import useNavigate from '../router/useNavigate';
import Toast from '../../components/Toast';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from './handleApolloError';
import type { Dispatch } from '../../app/redux/useAppSelector';
import { useAppDispatch } from '../../app/redux/useAppSelector';

interface UseErrorRedirectEffectOptions {
  redirectPath?: string;
  noun: string;
  error?: ApolloError;
}

const handleError = ({
  dispatch,
  error,
  noun,
  navigate,
  redirectPath = '/',
}: {
  dispatch: Dispatch;
  navigate: ReturnType<typeof useNavigate>;
  error: ApolloError;
} & UseErrorRedirectEffectOptions) => {
  handleApolloError(error, {
    NOT_FOUND: () => {
      const message = `${_.capitalize(noun)} not found`;
      dispatch(
        UIActions.pushToast(({ onDismiss }) => (
          <Toast
            level="error"
            message={message}
            onDismiss={onDismiss}
            title="Oops"
          />
        )),
      );
      navigate(redirectPath, { replace: true });
    },
    FORBIDDEN: () => {
      const message = `You do not have permission to view this ${noun}`;
      dispatch(UIActions.pushToast({ level: 'error', message }));
      navigate(redirectPath, { replace: true });
    },
  });
};

const useErrorRedirectEffect = (options: UseErrorRedirectEffectOptions) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (options.error) {
      handleError({ dispatch, ...options, error: options.error, navigate });
    }
  });
};

export default useErrorRedirectEffect;
